import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Tooltip
} from "@mui/material";
import Nav from "../common/Nav.jsx";
import FAQSection from "../common/FAQSection.jsx";
import { useLazyQuery, gql } from "@apollo/client";
import { getStatusDisplay } from '../../utils/statusUtils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';

const GET_GRIEVANCE_TRANSACTION_DETAILS = gql`
  query getGrievanceTransactionDetails($input: GrievanceTransactionDetailsFilter) {
    getGrievanceTransactionDetails(input: $input) {
      status
      message
      data {
        date
        amount
        pgRefId
        refundDetails
        transactionId
        mode
        statusMsg
        paymentId
        statusKey
        currency
        settlementAmount
        ticketId
        captureDate
        customerDetails
        status
        merchantName
      }
    }
  }
`;

const statusMessages = {
  'TXN_SUCCESS': {
    heading: "Payment was successful.",
    info: "Payment was successful.",
    icon: <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />, // green
    bg: "#E3FCEF"
  },
  'TXN_PENDING': {
    heading: "Your payment is pending",
    info: "Your payment is still being processed.",
    icon: <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 24 }} />, // yellow
    bg: "#FFF8E1"
  },
  'TXN_FAILURE': {
    heading: "Your payment failed",
    info: "Payment failed. Please try again or support.",
    icon: <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />, // red
    bg: "#FDEDED"
  },
  'TXN_REJECTED': {
    heading: "Your payment was rejected",
    info: "Payment was rejected. Please support.",
    icon: <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />, // red
    bg: "#FDEDED"
  },
  'TXN_CANCELLED': {
    heading: "Your payment was cancelled",
    info: "Payment was cancelled.",
    icon: <CancelIcon sx={{ color: 'action.disabled', fontSize: 24 }} />, // grey
    bg: "#F4F5F7"
  },
  'TXN_REFUNDED': {
    heading: "Your payment was refunded",
    info: "Payment was refunded.",
    icon: <ReplayIcon sx={{ color: 'info.main', fontSize: 24 }} />, // blue
    bg: "#E3FCEF"
  },
  'TXN_PROCESSING': {
    heading: "Your payment is processing",
    info: "Your payment is being processed.",
    icon: <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 24 }} />, // yellow
    bg: "#FFF8E1"
  },
  'TXN_INITIATED': {
    heading: "Your payment is initiated",
    info: "Your payment has been initiated.",
    icon: <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 24 }} />, // yellow
    bg: "#FFF8E1"
  },
  'TXN_UNKNOWN': {
    heading: "Payment status unknown",
    info: "The payment status could not be determined. Please support.",
    icon: <HelpOutlineIcon sx={{ color: 'action.disabled', fontSize: 24 }} />, // grey
    bg: "#F4F5F7"
  },
  'UNKNOWN': {
    heading: "Payment status unknown",
    info: "The payment status could not be determined. Please support.",
    icon: <HelpOutlineIcon sx={{ color: 'action.disabled', fontSize: 24 }} />, // grey
    bg: "#F4F5F7"
  },
  'default': {
    heading: "Payment status unknown",
    info: "The payment status could not be determined. Please support.",
    icon: <HelpOutlineIcon sx={{ color: 'action.disabled', fontSize: 24 }} />, // grey
    bg: "#F4F5F7"
  }
};

// Add a helper function for formatting
function formatDateTime(dateObj) {
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const min = String(dateObj.getMinutes()).padStart(2, '0');
  const ss = String(dateObj.getSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
}

const TransactionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paymentId } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [getTransactionDetails] = useLazyQuery(GET_GRIEVANCE_TRANSACTION_DETAILS);
  const [showFullStatusMsg, setShowFullStatusMsg] = useState(false);
  const [paymentIdCopied, setPaymentIdCopied] = useState(false);

  const handleCopyPaymentId = async () => {
    if (!paymentId) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(paymentId);
      } else {
        const ta = document.createElement('textarea');
        ta.value = paymentId;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setPaymentIdCopied(true);
      setTimeout(() => setPaymentIdCopied(false), 2000);
    } catch (e) {
      // silent fail
    }
  };

  // Get merchantId from navigation state or use default
  const merchantId = location.state?.merchantId || "merchantIdWeb";

  useEffect(() => {
    if (!paymentId) {
      setError('No transaction ID found in URL.');
      return;
    }
    setLoading(true);
    setError(null);
    getTransactionDetails({
      variables: { input: { paymentId, merchantId } },
      fetchPolicy: 'no-cache',
    })
      .then(({ data }) => {
        if (
          data.getGrievanceTransactionDetails.status === "SUCCESS" ||
          data.getGrievanceTransactionDetails.status === "OK"
        ) {
          setTransaction(data.getGrievanceTransactionDetails.data);
        } else {
          setError(data.getGrievanceTransactionDetails.message || "Failed to fetch details");
        }
      })
      .catch(() => {
        setError("Failed to fetch details");
      })
      .finally(() => setLoading(false));
  }, [paymentId]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', py: 10 }}>
      <CircularProgress size={48} color="primary" />
    </Box>
  );
  
  if (error) return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh"
      }}
    >
      <Nav />
      <Box
        sx={{
          maxWidth: 480,
          mx: 'auto',
          mt: 10,
          mb: 14,
          p: { xs: 3, sm: 5 },
          background: 'linear-gradient(135deg, #F8F9FA 60%, #E3F0FF 100%)',
          border: '1.5px solid #E1E5E9',
          borderRadius: '20px',
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.07)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: '#FDEDED',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.15)',
          }}
        >
          {error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch') ? (
            <WifiOffIcon sx={{ fontSize: 40, color: '#DC3545' }} />
          ) : (
            <ErrorOutlineIcon sx={{ fontSize: 40, color: '#DC3545' }} />
          )}
        </Box>
        
        <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#253858', mb: 1, letterSpacing: 0.2 }}>
          {error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch') 
            ? 'Connection Error' 
            : 'Transaction Not Found'}
        </Typography>
        
        <Box sx={{ width: '100%', my: 2 }}>
          <hr style={{ border: 'none', borderTop: '1.5px solid #E1E5E9', margin: 0 }} />
        </Box>
        
        <Typography sx={{ color: '#253858', fontSize: 18, mb: 2, fontWeight: 500 }}>
          {error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch')
            ? 'Unable to connect to our servers. Please check your internet connection and try again.'
            : error}
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => {
            // Clear sessionStorage for transactionId flow
            sessionStorage.removeItem('paymentIdFlow');
            sessionStorage.removeItem('lastPaymentId');
            sessionStorage.removeItem('bearerToken');
            
            // Clear localStorage
            localStorage.removeItem('accessToken');
            
            // Redirect to login page
            window.location.href = '/';
          }}
          sx={{
            background: 'linear-gradient(90deg, #3965FB 60%, #4F8FFF 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 16,
            px: 4,
            py: 1.5,
            borderRadius: '8px',
            boxShadow: '0 2px 8px 0 rgba(57,101,251,0.10)',
            mb: 3,
            textTransform: 'capitalize',
            '&:hover': {
              background: 'linear-gradient(90deg, #335ecc 60%, #3965FB 100%)',
            },
          }}
        >
          Try Again
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            borderColor: '#3965FB',
            color: '#3965FB',
            fontWeight: 600,
            fontSize: 16,
            px: 4,
            py: 1.5,
            borderRadius: '8px',
            mb: 3,
            textTransform: 'capitalize',
            '&:hover': {
              borderColor: '#335ecc',
              backgroundColor: 'rgba(57, 101, 251, 0.04)',
            },
          }}
        >
          Go Back
        </Button>
        
        <Typography sx={{ color: '#25385899', fontSize: 16, mb: 3, fontWeight: 400 }}>
          Still having trouble? Please write to us at
        </Typography>
        
        <a href="mailto:grievancesupport@pbpay.com" style={{ textDecoration: 'none' }}>
          <Box
            sx={{
              display: 'inline-block',
              background: 'linear-gradient(90deg, #3965FB 60%, #4F8FFF 100%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              px: 3,
              py: 1.2,
              borderRadius: '8px',
              boxShadow: '0 2px 8px 0 rgba(57,101,251,0.10)',
              mb: 2,
              transition: 'background 0.2s',
              ':hover': {
                background: 'linear-gradient(90deg, #335ecc 60%, #3965FB 100%)',
              },
            }}
          >
            grievancesupport@pbpay.com
          </Box>
        </a>
        
        <Typography sx={{ color: '#25385899', fontSize: 15, mt: 1 }}>
          Detailing your concerns along with details of the transaction.
        </Typography>
      </Box>
      <FAQSection />
    </Box>
  );
  
  if (!transaction || (transaction.amount === undefined || transaction.amount === null)) {
    return (
      <Box
        sx={{
          background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
          minHeight: "100vh"
        }}
      >
        <Nav />
        <Box
          sx={{
            maxWidth: 480,
            mx: 'auto',
            mt: 10,
            mb: 14,
            p: { xs: 3, sm: 5 },
            background: 'linear-gradient(135deg, #F8F9FA 60%, #E3F0FF 100%)',
            border: '1.5px solid #E1E5E9',
            borderRadius: '20px',
            boxShadow: '0 4px 24px 0 rgba(0,0,0,0.07)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#FFF3CD',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 4px 12px rgba(255, 193, 7, 0.15)',
            }}
          >
            <SentimentDissatisfiedIcon sx={{ fontSize: 40, color: '#856404' }} />
          </Box>
          
          <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#253858', mb: 1, letterSpacing: 0.2 }}>
            Invalid Transaction
          </Typography>
          
          <Box sx={{ width: '100%', my: 2 }}>
            <hr style={{ border: 'none', borderTop: '1.5px solid #E1E5E9', margin: 0 }} />
          </Box>
          
          <Typography sx={{ color: '#253858', fontSize: 18, mb: 2, fontWeight: 500 }}>
            No valid transaction details found. Please try again or support.
          </Typography>
          
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderColor: '#3965FB',
              color: '#3965FB',
              fontWeight: 600,
              fontSize: 16,
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              mb: 3,
              textTransform: 'capitalize',
              '&:hover': {
                borderColor: '#335ecc',
                backgroundColor: 'rgba(57, 101, 251, 0.04)',
              },
            }}
          >
            Go Back
          </Button>
          
          <Typography sx={{ color: '#25385899', fontSize: 16, mb: 3, fontWeight: 400 }}>
            Need help? Please write to us at
          </Typography>
          
          <a href="mailto:grievancesupport@pbpay.com" style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                display: 'inline-block',
                background: 'linear-gradient(90deg, #3965FB 60%, #4F8FFF 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                px: 3,
                py: 1.2,
                borderRadius: '8px',
                boxShadow: '0 2px 8px 0 rgba(57,101,251,0.10)',
                mb: 2,
                transition: 'background 0.2s',
                ':hover': {
                  background: 'linear-gradient(90deg, #335ecc 60%, #3965FB 100%)',
                },
              }}
            >
              grievancesupport@pbpay.com
            </Box>
          </a>
          
          <Typography sx={{ color: '#25385899', fontSize: 15, mt: 1 }}>
            Detailing your concerns along with details of the transaction.
          </Typography>
        </Box>
        <FAQSection />
      </Box>
    );
  }

  // Map API data to UI fields
  const statusMap = {
    "SUCCESS": "Success",
    "OK": "Success",
    "FAILED": "Failed",
    "PENDING": "Pending",
    "UNKNOWN": "Unknown"
  };
  const merchant = transaction.merchantName || transaction.merchant || 'Merchant';
  const amount = transaction.amount;
  const type = transaction.mode;
  const status = transaction.status && transaction.status !== "UNKNOWN"
    ? transaction.status
    : (statusMap[transaction.status] || transaction.status || "Unknown");
  const logo = '/path-to-logo.png'; // You can update this if your API provides a logo

  const statusKey = transaction.status || 'default';
  const statusMsgObj = statusMessages[statusKey] || statusMessages['default'];

  const rawTicketId = transaction.ticketId;
  const hasExistingTicket =
    rawTicketId != null && String(rawTicketId).trim() !== '';

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleContactSupport = () => {
    const isPaymentIdFlow = sessionStorage.getItem('paymentIdFlow') === 'true';
    navigate("/ticket", { state: { transaction, ...(isPaymentIdFlow ? { bypassAuth: true } : {}) } });
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh"
      }}
    >
      <Nav />

      <Box
        sx={{
          px: { xs: "16px", sm: "70px" },
          py: { xs: 3, sm: 4 },
          mx: "auto",
          maxWidth: "1440px"
        }}
      >
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{ display: { xs: "none",sm:'none', md: "block" } }}
          >
            <Typography
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "24px",
                color: "#253858"
              }}
            >
              Payment Status
            </Typography>

            <Card
              sx={{
                width: "100%",
                boxShadow: "none",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar src={logo} sx={{ width: 48, height: 48, mr: 2 }} />
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400, color: "#253858" }}
                    >
                      {merchant}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                    <Chip
                      label={getStatusDisplay(transaction.status).label}
                      sx={{
                        backgroundColor: getStatusDisplay(transaction.status).backgroundColor,
                        color: getStatusDisplay(transaction.status).color,
                        height: 22,
                        borderRadius: "100px",
                        fontWeight: 700,
                        fontSize: "12px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#000000",
                      }}
                    >
                      ₹{(Number(amount) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>

              <Box
                sx={{
                  backgroundColor: "#F4F5F7",
                  borderTop: "1px dashed #DFE1E6",
                  px: 2,
                  py: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      color: "#253858",
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {type} • {paymentId}
                  </Typography>
                  <Tooltip title={paymentIdCopied ? 'Copied!' : 'Copy Payment ID'} open={paymentIdCopied || undefined} placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={handleCopyPaymentId}
                      aria-label="Copy Payment ID"
                      sx={{ p: 0.25, color: '#5E6C84', '&:hover': { color: '#0065FF' } }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#253858",
                    opacity: 0.6
                  }}
                >
                  {formatDateTime(new Date((transaction.date || '').replace(' ', 'T')))}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={12} md={8}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <IconButton onClick={handleGoBack} sx={{ p: 0, mr: 1 }}>
                <img src="/Icons/Goback.svg" alt="svg" />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "20px",
                  lineHeight: "24px",
                  color: "#253858"
                }}
              >
                Transaction Details
              </Typography>
            </Box>

            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "20px",
                boxShadow: "none",
                mb: 3
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#253858",
                  mb: statusKey === 'TXN_SUCCESS' ? 2 : 1
                }}
              >
                {statusMsgObj.heading}
              </Typography>
              {statusKey !== 'TXN_SUCCESS' && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#25385899",
                  lineHeight: "24px",
                  mb: 2
                }}
              >
                {statusKey === 'TXN_FAILURE' && (
                  <>Your payment of ₹{(Number(amount) / 100).toFixed(2)} to {merchant} failed. Please try again or support for assistance.</>
                )}
                {statusKey === 'TXN_INITIATED' && (
                  <>Your payment of ₹{(Number(amount) / 100).toFixed(2)} to {merchant} has been initiated. Please wait for confirmation. If you have any queries, support.</>
                )}
                {statusKey === 'TXN_PENDING' && (
                  <>Your payment of ₹{(Number(amount) / 100).toFixed(2)} to {merchant} is pending. Please wait while we process your payment. For any queries, support.</>
                )}
                {![
                  'TXN_SUCCESS',
                  'TXN_FAILURE',
                  'TXN_INITIATED',
                  'TXN_PENDING'
                ].includes(statusKey) && statusMsgObj.info}
              </Typography>
              )}

              <Box
                sx={{
                  border: "1px solid #DFE1E6",
                  p: { xs: 2, md: 3 },
                  borderRadius: "8px",
                  mt: 2,
                  mb: 3
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#253858", display: "flex", gap: "20px" }}
                >
                  {merchant}
                  <Chip
                    label={getStatusDisplay(transaction.status).label}
                    sx={{
                      borderRadius: "100px",
                      backgroundColor: getStatusDisplay(transaction.status).backgroundColor,
                      color: getStatusDisplay(transaction.status).color,
                      fontWeight: 700,
                      height: 22,
                      fontSize: "12px"
                    }}
                  />
                </Typography>

                <hr
                  style={{
                    border: "1px solid #DFE1E6",
                    margin: "10px 0"
                  }}
                />
                <Grid container spacing={4}>
                  <Grid item xs={6} sm={4}>
                    <Typography sx={{ color: "#253858", opacity: 0.6, fontSize: "14px" }}>
                      Transaction date and time
                    </Typography>
                    <Typography sx={{ color: "#253858", fontWeight: 500,fontSize:{xs:'12px',md:'14px'}, }}>
                      {formatDateTime(new Date((transaction.date || '').replace(' ', 'T')))}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Typography sx={{ color: "#253858", opacity: 0.6, fontSize: "14px" }}>
                      Payment Amount
                    </Typography>
                    <Typography sx={{ color: "#253858", fontWeight: 500,fontSize:{xs:'12px',md:'14px'}, }}>
                      ₹{(Number(amount) / 100).toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Typography sx={{ color: "#253858", opacity: 0.6, fontSize: "14px" }}>
                      Payment Method
                    </Typography>
                    <Typography sx={{ color: "#253858", fontWeight: 500,fontSize:{xs:'12px',md:'14px'}, }}>
                      {type}
                    </Typography>
                  </Grid>
                </Grid>

                {statusKey !== 'TXN_SUCCESS' && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: statusMsgObj.bg,
                    p: 2,
                    borderRadius: "8px",
                    mt: 3
                  }}
                >
                  {statusMsgObj.icon}
                  <Typography
                    sx={{
                      ml: 1,
                      fontWeight: 500,
                      fontSize: "12px",
                      color: "#253858"
                    }}
                  >
                    {statusMsgObj.info}
                  </Typography>
                </Box>
                )}
              </Box>

              {hasExistingTicket ? (
                <Box
                  sx={{
                    border: "1px solid #DFE1E6",
                    p: 2,
                    borderRadius: "8px",
                    mb: 3
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "#253858" }}
                  >
                    Ticket already created
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#25385899", mt: 1, lineHeight: "20px" }}
                  >
                    Ticket is already created with ticket ID: {rawTicketId}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid #DFE1E6",
                    p: 2,
                    borderRadius: "8px",
                    mb: 3
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "#253858" }}
                  >
                    Reach out to {merchant} support
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#25385899", mt: 1 }}
                  >
                    Reach out to {merchant} support for any update on the service/goods.{" "}
                    <Button
                      onClick={handleContactSupport}
                      sx={{
                        textTransform: "none",
                        color: "#0065FF",
                        p: 0,
                        minWidth: "auto"
                      }}
                    >
                      support
                    </Button>
                  </Typography>
                </Box>
              )}

              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#25385899",
                  mt: "20px"
                }}
              >
                Awaiting a response to an earlier query? {" "}
                <Typography
                  component="span"
                  onClick={() => {
                    localStorage.removeItem('accessToken');
                    navigate('/check-query', { state: { bypassAuth: true } });
                  }}
                  sx={{ color: "#0065FF", fontWeight: 500, cursor: "pointer" }}
                >
                  Track Existing Query
                </Typography>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <FAQSection/>
    </Box>
  );
};

export default TransactionDetails;
